/**
 * TanStack Query hooks for project recap notes.
 *
 * The crew capture notes (text + photos) on the task screen during a job —
 * internal source material. At closeout the supervisor synthesizes them into a
 * single narrative blurb and curates a photo gallery (`useUpdateRecapGallery`);
 * the crew note text itself never ships to the requester. Photos upload via the
 * shared project-media endpoint, then ride along as attachment_ids.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RecapGalleryResponse,
  RecapGalleryUpdate,
  RecapNote,
  RecapNoteCreate,
  RecapNoteListResponse,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

function listKey(projectId: string) {
  return ['recap-notes', projectId] as const;
}

export function useRecapNotes(projectId: string | undefined) {
  return useQuery<RecapNoteListResponse>({
    queryKey: listKey(projectId ?? ''),
    enabled: !!projectId,
    queryFn: () => apiCall<RecapNoteListResponse>(`/projects/${projectId}/recap-notes`),
    staleTime: 10_000,
  });
}

export function useAddRecapNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation<RecapNote, Error, RecapNoteCreate>({
    mutationFn: (body) =>
      apiCall<RecapNote>(`/projects/${projectId}/recap-notes`, { method: 'POST', body }),
    // Recap notes are internal capture — no @mentions, no notifications.
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(projectId) }),
  });
}

export function useDeleteRecapNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationFn: (noteId) =>
      apiCall<{ ok: boolean }>(`/projects/${projectId}/recap-notes/${noteId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(projectId) }),
  });
}

/**
 * Save the supervisor's curated, ordered recap photo gallery (the images opted
 * into the closeout recap). Refreshes the assembled recap so the metrics + PDF
 * reflect the new selection.
 */
export function useUpdateRecapGallery(projectId: string) {
  const qc = useQueryClient();
  return useMutation<RecapGalleryResponse, Error, RecapGalleryUpdate>({
    mutationFn: (body) =>
      apiCall<RecapGalleryResponse>(`/projects/${projectId}/recap/gallery`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', 'recap', projectId] }),
  });
}
